/**
 * Camera Types
 * Type definitions for camera settings and profiles
 */
import { CAMERA_DEFAULTS } from '../../../shared/constants/GridConstants';

export interface CameraSettings {
  zoom: number;
  minZoom: number;
  maxZoom: number;
  followSpeed: number;
  lookaheadX: number;
  lookaheadY: number;
  smoothing: number;
  lockX: boolean;
  lockY: boolean;
  offsetX: number;
  offsetY: number;
  deadzoneWidth: number;
  deadzoneHeight: number;
  // Idle lock settings (fixes jump wobble)
  idleLock?: boolean;
  idleLockThreshold?: number;
  idleLockSmoothing?: number;
  idleDeadzoneX?: number;
  idleDeadzoneY?: number;
}

export interface CameraProfile {
  name: string;
  settings: CameraSettings;
  createdAt: number;
  updatedAt: number;
}

export type CameraProfileMap = Record<string, CameraProfile>;

export const DEFAULT_CAMERA_SETTINGS: CameraSettings = {
  zoom: CAMERA_DEFAULTS.ZOOM,
  minZoom: CAMERA_DEFAULTS.MIN_ZOOM,
  maxZoom: CAMERA_DEFAULTS.MAX_ZOOM,
  followSpeed: CAMERA_DEFAULTS.FOLLOW_SPEED,
  lookaheadX: CAMERA_DEFAULTS.LOOKAHEAD_X,
  lookaheadY: CAMERA_DEFAULTS.LOOKAHEAD_Y,
  smoothing: CAMERA_DEFAULTS.SMOOTHING,
  lockX: false,
  lockY: false,
  offsetX: CAMERA_DEFAULTS.OFFSET_X,
  offsetY: CAMERA_DEFAULTS.OFFSET_Y,
  deadzoneWidth: CAMERA_DEFAULTS.DEADZONE_WIDTH,
  deadzoneHeight: CAMERA_DEFAULTS.DEADZONE_HEIGHT,
  // Idle lock defaults (fixes jump wobble)
  idleLock: CAMERA_DEFAULTS.IDLE_LOCK_ENABLED,
  idleLockThreshold: CAMERA_DEFAULTS.IDLE_LOCK_THRESHOLD,
  idleLockSmoothing: CAMERA_DEFAULTS.IDLE_LOCK_SMOOTHING,
  idleDeadzoneX: CAMERA_DEFAULTS.IDLE_DEADZONE_X,
  idleDeadzoneY: CAMERA_DEFAULTS.IDLE_DEADZONE_Y,
};

// Validate settings - only enforce non-negative values, no artificial limits
export function validateCameraSettings(settings: Partial<CameraSettings>): CameraSettings {
  return {
    ...DEFAULT_CAMERA_SETTINGS,
    ...settings,
    // Only prevent negative/zero values, no max limits
    zoom: Math.max(0.1, settings.zoom ?? CAMERA_DEFAULTS.ZOOM),
    minZoom: Math.max(0.1, settings.minZoom ?? CAMERA_DEFAULTS.MIN_ZOOM),
    maxZoom: Math.max(0.1, settings.maxZoom ?? CAMERA_DEFAULTS.MAX_ZOOM),
    followSpeed: Math.max(0.01, settings.followSpeed ?? CAMERA_DEFAULTS.FOLLOW_SPEED),
    deadzoneWidth: Math.max(0, settings.deadzoneWidth ?? CAMERA_DEFAULTS.DEADZONE_WIDTH),
    deadzoneHeight: Math.max(0, settings.deadzoneHeight ?? CAMERA_DEFAULTS.DEADZONE_HEIGHT),
    idleLockThreshold: Math.max(0, settings.idleLockThreshold ?? CAMERA_DEFAULTS.IDLE_LOCK_THRESHOLD),
    idleLockSmoothing: Math.max(0.001, settings.idleLockSmoothing ?? CAMERA_DEFAULTS.IDLE_LOCK_SMOOTHING),
    idleDeadzoneX: Math.max(0, settings.idleDeadzoneX ?? CAMERA_DEFAULTS.IDLE_DEADZONE_X),
    idleDeadzoneY: Math.max(0, settings.idleDeadzoneY ?? CAMERA_DEFAULTS.IDLE_DEADZONE_Y),
  };
}

// Preset camera profiles (MapleStory-style, Cinematic, etc.)
export const PRESET_CAMERA_PROFILES: CameraProfileMap = {
  'MapleStory Classic': {
    name: 'MapleStory Classic',
    settings: {
      zoom: 1,
      minZoom: 0.5,
      maxZoom: 2,
      followSpeed: 0.15,
      lookaheadX: 80,
      lookaheadY: 0, // No vertical lookahead - on rails
      smoothing: 0.1,
      lockX: false,
      lockY: true, // Lock Y axis - camera on rails
      offsetX: 0,
      offsetY: -50, // Fixed height above character
      deadzoneWidth: 60,
      deadzoneHeight: 0 // No vertical deadzone - pure horizontal
    },
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  'Cinematic Wide': {
    name: 'Cinematic Wide',
    settings: {
      zoom: 0.6,
      minZoom: 0.4,
      maxZoom: 1.5,
      followSpeed: 0.05,
      lookaheadX: 100,
      lookaheadY: 50,
      smoothing: 0.15,
      lockX: false,
      lockY: false,
      offsetX: 0,
      offsetY: 0,
      deadzoneWidth: 150,
      deadzoneHeight: 80
    },
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  'Action Close-Up': {
    name: 'Action Close-Up',
    settings: {
      zoom: 1.5,
      minZoom: 1,
      maxZoom: 3,
      followSpeed: 0.25,
      lookaheadX: 40,
      lookaheadY: 20,
      smoothing: 0.05,
      lockX: false,
      lockY: false,
      offsetX: 0,
      offsetY: -30,
      deadzoneWidth: 40,
      deadzoneHeight: 30
    },
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  'Platformer Classic': {
    name: 'Platformer Classic',
    settings: {
      zoom: 1.2,
      minZoom: 0.8,
      maxZoom: 2,
      followSpeed: 0.2,
      lookaheadX: 60,
      lookaheadY: 80, // Look down for platforms
      smoothing: 0.08,
      lockX: false,
      lockY: false,
      offsetX: 0,
      offsetY: 20,
      deadzoneWidth: 80,
      deadzoneHeight: 100
    },
    createdAt: Date.now(),
    updatedAt: Date.now()
  },
  'Boss Battle': {
    name: 'Boss Battle',
    settings: {
      zoom: 0.9,
      minZoom: 0.6,
      maxZoom: 1.8,
      followSpeed: 0.12,
      lookaheadX: 0, // Center on player
      lookaheadY: 0,
      smoothing: 0.1,
      lockX: false,
      lockY: false,
      offsetX: 0,
      offsetY: 0,
      deadzoneWidth: 200, // Large deadzone for arena
      deadzoneHeight: 120
    },
    createdAt: Date.now(),
    updatedAt: Date.now()
  }
};
